import React from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useApp } from '@/context/AppContext';
import { useOffline } from '@/context/OfflineContext';
import { ClockWidget } from '@/components/ClockWidget';
import {
  House,
  SignOut,
  Package,
  UserCircle,
  CaretDown,
  Bell,
  WifiHigh,
  WifiSlash,
  CloudArrowUp,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { outlets, currentOutlet, setCurrentOutlet } = useApp();
  const { isOnline, pendingCount, isSyncing, flushQueue } = useOffline();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isDashboard = location.pathname === '/dashboard';

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--theme-background)' }}>
      {/* Top bar (replaces left sidebar) */}
      <header
        className="h-16 border-b flex items-center justify-between px-6 sticky top-0 z-30"
        style={{
          backgroundColor: 'var(--theme-surface)',
          borderColor: 'var(--theme-border)',
        }}
        data-testid="top-bar"
      >
        <div className="flex items-center gap-4">
          {/* Logo / Home */}
          <NavLink
            to="/dashboard"
            className="font-heading text-xl font-bold tracking-tight"
            style={{ color: 'var(--theme-accent)' }}
            data-testid="brand-home"
          >
            Restro POS
          </NavLink>

          {/* Back-to-dashboard button (only when not on dashboard) */}
          {!isDashboard && (
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm transition-colors"
              style={{
                backgroundColor: 'var(--theme-background)',
                color: 'var(--theme-text)',
              }}
              data-testid="back-to-dashboard"
            >
              <House size={16} weight="bold" />
              Dashboard
            </button>
          )}

          {/* Outlet selector */}
          {outlets.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 px-4 py-2 rounded-xl transition-colors"
                  style={{
                    backgroundColor: 'var(--theme-background)',
                    color: 'var(--theme-text)',
                  }}
                  data-testid="outlet-selector"
                >
                  <Package size={18} style={{ color: 'var(--theme-accent)' }} />
                  <span className="text-sm font-medium">{currentOutlet?.name || 'Select Outlet'}</span>
                  <CaretDown size={16} style={{ color: 'var(--theme-text-muted)' }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {outlets.map((outlet) => (
                  <DropdownMenuItem
                    key={outlet.id}
                    onClick={() => setCurrentOutlet(outlet)}
                    className={currentOutlet?.id === outlet.id ? 'bg-[rgba(var(--theme-accent-rgb),0.1)]' : ''}
                  >
                    <span>{outlet.name}</span>
                    <span className="ml-auto text-xs capitalize" style={{ color: 'var(--theme-text-muted)' }}>
                      {outlet.mode}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Clock In/Out widget */}
          <ClockWidget />

          {/* Online status */}
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
            style={{
              backgroundColor: isOnline
                ? 'rgba(var(--theme-success-rgb), 0.15)'
                : 'rgba(239, 68, 68, 0.15)',
              color: isOnline ? 'var(--theme-success)' : 'var(--theme-error)',
            }}
            data-testid="online-status"
          >
            {isOnline ? <WifiHigh size={16} /> : <WifiSlash size={16} />}
            {isOnline ? 'Online' : 'Offline'}
          </div>

          {/* Pending sync badge */}
          {pendingCount > 0 && (
            <button
              onClick={flushQueue}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-all hover:scale-105"
              style={{
                backgroundColor: 'rgba(251, 191, 36, 0.15)',
                borderColor: 'rgba(251, 191, 36, 0.4)',
                color: '#b45309',
              }}
              data-testid="pending-sync-badge"
              title="Click to retry sync now"
            >
              <CloudArrowUp size={14} className={isSyncing ? 'animate-pulse' : ''} />
              {pendingCount} queued
            </button>
          )}

          {/* Notifications */}
          <button
            className="relative p-2 rounded-lg transition-colors"
            style={{ color: 'var(--theme-text-muted)' }}
            data-testid="notifications-btn"
          >
            <Bell size={20} />
            <span
              className="absolute top-1 right-1 w-2 h-2 rounded-full"
              style={{ backgroundColor: 'var(--theme-error)' }}
            ></span>
          </button>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 px-3 py-1.5 rounded-xl transition-colors"
                style={{
                  backgroundColor: 'var(--theme-background)',
                  color: 'var(--theme-text)',
                }}
                data-testid="user-menu-trigger"
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(var(--theme-accent-rgb), 0.15)' }}
                >
                  <UserCircle size={20} style={{ color: 'var(--theme-accent)' }} />
                </div>
                <div className="hidden sm:flex flex-col items-start leading-tight">
                  <span className="text-xs font-medium truncate max-w-[120px]">{user?.name}</span>
                  <span className="text-[10px] capitalize" style={{ color: 'var(--theme-text-muted)' }}>
                    {user?.role?.replace('_', ' ')}
                  </span>
                </div>
                <CaretDown size={14} style={{ color: 'var(--theme-text-muted)' }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                <House size={16} className="mr-2" /> Dashboard
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <SignOut size={16} className="mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Page content */}
      <main className="flex-1 p-6">{children}</main>
    </div>
  );
};

export default Layout;
